import React, { PureComponent } from 'react';
import QueryManager from 'src/lib/managers/QueryManager';
import PropTypes from 'prop-types';
import isMobile from 'is-mobile';
import { FILTERS, FRESH_THEME, INIT_SEARCH_TIMESTAMP, RUSTY_THEME, YAHOO_VENDOR } from 'src/lib/constants/general';
import debug from 'src/lib/logger';
import withParsedQuery from 'src/lib/hoc/withParsedQuery';
import Router from 'next/router';
import routes, { SEARCH } from 'src/lib/constants/routes';
import {
	getCurrentTheme,
	setComplaintMode,
	setCurrentTheme,
	setIsDiscoveryMode,
	setBackToTop,
	getCurrentCategoryPage,
	setCurrentCategoryPage,
	setCurrentDevice,
	getCurrentPage,
} from 'src/lib/redux/modules/settings';
import { connect } from 'react-redux';
import Head from 'src/components/Head/Head';
import TrackingManager from 'src/lib/managers/TrackingManager';
import {
	getCurrentQuery,
	getIsDomReady,
	getIsLoadedMoreProducts,
	getIsLoadingMoreProducts,
	getIsLoadingProducts,
	getIsNoResultsAtAll,
	getYahooProps,
	searchProducts,
	setCurrentQuery,
	setIsFilter,
	setIsLoadingProducts,
	setIsNoResults,
	setVendorProps,
} from 'src/lib/redux/modules/search';
import dynamic from 'next/dynamic';
import pageTracker from 'src/lib/hoc/pageTracker';
import PerformanceManager from 'src/lib/managers/PerformanceManager';
import { getUserClicksAmount } from 'src/lib/redux/modules/user';
import LazyLoad, { forceVisible } from 'react-lazyload';
import NewsLetters from '../NewsLetters/NewsLetters';
import withWhoAmI from 'src/lib/hoc/withWhoAmI';
import { COMPLAINT_CIDS, DISCOVERY_CIDS } from 'src/lib/constants/cids';
import ScrollFlow from '../ScrollFlow';
import { PERSIST_VENDOR_PROPS, SESSION_START } from 'src/lib/constants/storage';
import { getSession, setSession } from 'src/lib/managers/StorageManager';
import { addYahooBeacon } from 'src/lib/helpers';
import { yahooScript } from 'src/lib/constants/scripts';

PerformanceManager.set(INIT_SEARCH_TIMESTAMP, Date.now());

const Header = dynamic(() =>
	import(/* webpackMode: "lazy" */ /* webpackChunkName: "fresh-header" */ /* webpackPreload: true */ 'src/components/Header/Header')
);

const Feed = dynamic(() =>
	import(/* webpackMode: "lazy" */ /* webpackChunkName: "feed" */ /* webpackPreload: true */ 'src/components/Feed/Feed')
);

const BackToTop = dynamic(
	() => import(/* webpackMode: "lazy" */ /* webpackChunkName: "back-to-top" */ 'src/components/BackToTop/BackToTop'),
	{ ssr: false }
);

const Footer = dynamic(() => import(/* webpackMode: "lazy" */ /* webpackChunkName: "footer" */ 'src/components/Footer/Footer'), {
	ssr: false,
});

const FeedbackToast = dynamic(
	() => import(/* webpackMode: "lazy" */ /* webpackChunkName: "feedback-toast" */ 'src/components/FeedbackToast/FeedbackToast'),
	{ ssr: false }
);

const prefixStyles = 'FP_';

@pageTracker()
@withParsedQuery()
@withWhoAmI()
@connect(
	state => ({
		currentQuery: getCurrentQuery(state.search),
		currentTheme: getCurrentTheme(state.settings),
		isLoadingProducts: getIsLoadingProducts(state.search),
		yahooBeacon: getYahooProps(state.search),
		userClicksAmount: getUserClicksAmount(state.user),
		currentCategory: getCurrentCategoryPage(state.settings),
		isNoResultsAtAll: getIsNoResultsAtAll(state.search),
		isLoadingMoreProducts: getIsLoadingMoreProducts(state.search),
		isLoadedMoreProducts: getIsLoadedMoreProducts(state.search),
		isDomReady: getIsDomReady(state.search),
		currentPage: getCurrentPage(state.settings),
	}),
	dispatch => ({
		dispatch,
	})
)
class FeedPage extends PureComponent {
	static propTypes = {
		currentQuery: PropTypes.string,
		parsedQuery: PropTypes.object,
		dispatch: PropTypes.func.isRequired,
		fullComplaintMode: PropTypes.bool,
		currentTheme: PropTypes.string,
		isLoadingProducts: PropTypes.bool,
		currentCategory: PropTypes.object,
		whoami: PropTypes.object,
		isFeedRendered: PropTypes.bool,
	};

	static defaultProps = {
		currentQuery: '',
		parsedQuery: {},
		fullComplaintMode: false,
		currentTheme: null,
		isLoadingProducts: true,
		currentCategory: {},
		whoami: {},
		isFeedRendered: false,
	};

	constructor(props) {
		super(props);
		this.state = {
			backToTop: false,
			reignnAdded: false,
		};
	}

	componentDidUpdate = () => {
		if (this.props.isDomReady) {
			this.checkIfScrollable().then(isScrollable => {
				if (!isScrollable && !isMobile()) {
					debug('force lazyload');
					forceVisible();
				}
			});
		}
		// TODO
		// if (this.isFirstFeedLoad(prevProps) || this.isMoreFeedLoad(prevProps)) {
		// 	addYahooBeacon(this.props.yahooBeacon);
		// }
	};

	isFirstFeedLoad = prevProps => {
		const yahooBeaconPrevPropsSize = Object.keys(prevProps.yahooBeacon).length;
		return (
			!this.props.isLoadedMoreProducts && this.props.yahooBeacon &&
			((yahooBeaconPrevPropsSize === 0 && this.props.yahooBeacon.ysid) ||
				(yahooBeaconPrevPropsSize > 0 && this.props.yahooBeacon.ysid && prevProps.yahooBeacon.ysid !== this.props.yahooBeacon.ysid))
		);
	};

	isMoreFeedLoad = prevProps => {
		return (
			prevProps.yahooBeacon.ysid &&
			this.props.yahooBeacon &&
			this.props.yahooBeacon.ysid &&
			prevProps.yahooBeacon.ysid !== this.props.yahooBeacon.ysid &&
			!this.props.isLoadingMoreProducts &&
			this.props.isLoadedMoreProducts
		);
	};

	checkIfScrollable = () => {
		return new Promise(resolve => {
			setTimeout(() => {
				resolve(window.innerHeight < document.body.scrollHeight);
			}, 200);
		});
	};

	searchInCategory = query => {
		const { parsedQuery } = this.props;
		const eventName = 'search';
		const eventProps = {
			keyword: query,
			cid: parsedQuery.cid,
			action: 'search',
			section: 'Category Page',
		};
		TrackingManager.trackOnNav(eventName, eventProps);
		parsedQuery.q = query;
		parsedQuery.page = '1';
		const url = `${SEARCH}?${QueryManager.stringify(parsedQuery)}`;
		location.href = url;
	};

	componentDidMount = () => {
		const { parsedQuery } = this.props;

		debug(`Yahoo Flow: 1 Fetch XMLP script`);

		const fullComplaintMode = COMPLAINT_CIDS.indexOf(parsedQuery.cid) > -1;
		this.checkIsOpenFilter();

		const currentTheme = isMobile() ? FRESH_THEME : RUSTY_THEME;
		const isInMobile = isMobile({ tablet: false });
		this.props.dispatch(setCurrentDevice({ mobile: isInMobile, desktop: !isInMobile }));
		this.props.dispatch(setCurrentTheme(currentTheme));

		const currentKeyword = parsedQuery.q;
		if (this.isDiscovery() && isInMobile) {
			this.props.dispatch(setIsDiscoveryMode(true));
		}

		if (fullComplaintMode) {
			this.props.dispatch(setComplaintMode(true));
		}

		this.initProducts(currentKeyword || null);
		window.addEventListener('scroll', this.scrollHandler);

		if (Router.router.route === routes.PREFIX_SLUG && this.props.yahooBeacon?.ysid) {
			// TODO - check
			addYahooBeacon(this.props.yahooBeacon);
		}
	};

	checkIsOpenFilter = () => {
		const { parsedQuery, dispatch } = this.props;
		const isOpenFilter = FILTERS.filter(filter => parsedQuery[filter]).length > 0;
		dispatch(setIsFilter(isOpenFilter));
	};

	isDiscovery = () => {
		const { parsedQuery } = this.props;
		return parsedQuery && parsedQuery.cid && DISCOVERY_CIDS.includes(parsedQuery.cid) && parsedQuery.vendors === YAHOO_VENDOR;
	};

	initProducts = currentKeyword => {
		const { dispatch, parsedQuery, currentCategory } = this.props;

		/**
		 * this handles the case where the page is loaded with no query param
		 * nothing we can do, so bail out early
		 */
		if (!currentKeyword && !Object.keys(currentCategory).length) {
			dispatch(setIsLoadingProducts(false));
			dispatch(setIsNoResults());
			return;
		}

		// get the current query params from the browser `location.search`
		// if there's no category, then search normally, otherwise search by category
		let currentQuery = '';
		let fetchParams = {};
		const isSessionStart = getSession(SESSION_START) !== 'true';

		if (!Object.keys(currentCategory).length) {
			currentQuery = currentKeyword || '';
			fetchParams = { ...parsedQuery, q: currentKeyword || '', sessionStart: isSessionStart };
			if (parsedQuery.vprops) {
				const vProps = getSession(PERSIST_VENDOR_PROPS);
				const vPropsObj = vProps ? JSON.parse(vProps) : {};
				dispatch(setVendorProps(vPropsObj));
			}
		} else {
			currentQuery = QueryManager.unslugify(currentCategory.categoryName).replace(/\s+/g, ' ') || '';
			fetchParams = { ...parsedQuery, sessionStart: isSessionStart, categoryId: currentCategory.categoryId };
		}

		if (Router.router && Router.router.route === routes.PREFIX_SLUG) {
			// SSR feed
			return;
		}

		dispatch(setCurrentQuery(currentQuery));
		dispatch(searchProducts(fetchParams));
		setSession(SESSION_START, true);
	};

	umountProducts = () => {
		const { dispatch } = this.props;
		dispatch(setCurrentQuery(''));
		dispatch(setVendorProps({}));
		dispatch(setCurrentCategoryPage({}));
	};

	componentWillUnmount = () => {
		this.umountProducts();
		window.removeEventListener('scroll', this.scrollHandler);
	};

	scrollHandler = () => {
		if (window.pageYOffset >= 100 && !this.state.backToTop) {
			this.setState({
				backToTop: true,
			});
		} else if (window.pageYOffset < 100 && this.state.backToTop) {
			this.props.dispatch(setBackToTop(false));
			this.setState({
				backToTop: false,
			});
		}
	};

	render = () => {
		const { currentTheme, parsedQuery, userClicksAmount, currentCategory, whoami } = this.props;
		const { cid, campaignid = '', gclid = '' } = parsedQuery;
		const { backToTop } = this.state;
		const { isHap } = whoami;
		const isDisplayToast = !isHap && Boolean((+cid === 4778 || +cid === 8111 || +cid === 5228) && gclid.length > 12 && campaignid);

		return (
			<div className={`${currentTheme}-theme`}>
				<Head>
					<script
						async={'async'}
						type="text/javascript"
						dangerouslySetInnerHTML={{
							__html: yahooScript,
						}}
					/>
				</Head>
				<Header onSearch={Object.keys(currentCategory).length ? this.searchInCategory : null} />
				<div className={prefixStyles + 'wrapper'}>
					<Feed />
					<ScrollFlow />
				</div>
				<LazyLoad once={true} offset={10}>
					<NewsLetters />
					<Footer />
				</LazyLoad>
				{backToTop && <BackToTop />}
				{isDisplayToast && userClicksAmount >= 3 ? <FeedbackToast /> : null}
			</div>
		);
	};
}

export default FeedPage;
